import React from 'react';
import { cn } from '../../lib/utils';
import LoadingSkeleton from './LoadingSkeleton';
import ErrorState from './ErrorState';
import EmptyState from './EmptyState';

/**
 * Page-scoped state wrappers.
 *
 * These compose the existing `LoadingSkeleton` / `ErrorState` / `EmptyState`
 * primitives and add the page-level layout concern (consistent min-height +
 * vertical centering) that was previously copy-pasted onto each page. Use them
 * for whole-page states; keep the bare primitives for inline / section states.
 *
 * RTL/Arabic copy and the design-system styling are inherited from the wrapped
 * primitives — these wrappers add layout only.
 */

const PAGE_MIN_H = 'min-h-[320px]';

/**
 * PageLoadingState — full-page loading placeholder.
 *
 * @param {'table'|'card'|'list'|'form'|'stats'|'page'} [variant='page']
 * @param {number} [count=3]
 * @param {string} [className]
 */
export const PageLoadingState = ({ variant = 'page', count = 3, className = '' }) => (
  <div className={cn('w-full', className)} data-testid="page-loading-state">
    <LoadingSkeleton variant={variant} count={count} />
  </div>
);

/**
 * PageErrorState — centered error block with retry.
 *
 * @param {Error|string} [error]
 * @param {() => void} [onRetry]
 * @param {string} [title]
 * @param {string} [className]
 */
export const PageErrorState = ({ error = null, onRetry = null, title, className = '' }) => (
  <div
    className={cn('flex items-center justify-center', PAGE_MIN_H, className)}
    data-testid="page-error-state"
  >
    <ErrorState error={error} onRetry={onRetry} title={title} />
  </div>
);

/**
 * PageEmptyState — centered empty block with optional CTA.
 *
 * @param {React.ComponentType} [icon]
 * @param {string} title
 * @param {string} [description]
 * @param {string} [actionLabel]
 * @param {() => void} [onAction]
 * @param {string} [secondaryLabel]
 * @param {() => void} [onSecondary]
 * @param {string} [className]
 */
export const PageEmptyState = ({
  icon = null,
  title = 'لا توجد بيانات',
  description = '',
  actionLabel = '',
  onAction = null,
  secondaryLabel = '',
  onSecondary = null,
  className = '',
}) => (
  <div
    className={cn('flex items-center justify-center', PAGE_MIN_H, className)}
    data-testid="page-empty-state"
  >
    <EmptyState
      icon={icon}
      title={title}
      description={description}
      actionLabel={actionLabel}
      onAction={onAction}
      secondaryLabel={secondaryLabel}
      onSecondary={onSecondary}
    />
  </div>
);
